import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@mellify/ui';
import { PlatformIcon } from './PlatformIcon';
import { Shield, Check, ExternalLink, Key } from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { connectDevto } from '../../../../services/socialAccountCallbackService';

interface ConnectDevtoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const permissions = [
  { label: 'Read Profile', description: 'Access your DEV Community profile information' },
  { label: 'Publish Articles', description: 'Create and publish articles on your behalf' },
  { label: 'Manage Articles', description: 'Edit and update your existing articles' },
  { label: 'Read Analytics', description: 'Access article views, reactions, and comment counts' },
  { label: 'Read Followers', description: 'Access your follower information' },
];

export const ConnectDevtoModal: React.FC<ConnectDevtoModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  const handleConnect = useCallback(async () => {
    if (!apiKey.trim()) {
      toast.error('Missing API Key', 'Please enter your Dev.to API key.');
      return;
    }

    const workspaceId = currentWorkspace?.id;
    if (!workspaceId) {
      toast.error('No Workspace', 'Please select a workspace first.');
      return;
    }

    setIsConnecting(true);

    try {
      const result = await connectDevto({
        workspaceId,
        apiKey: apiKey.trim(),
      });

      toast.success(
        'DEV Community Connected!',
        `${result.account.accountName || 'Your Dev.to account'} has been connected successfully.`
      );

      // Invalidate social accounts queries
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['social-account-health'] });

      setApiKey('');
      onSuccess();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to connect your Dev.to account.';

      if (message.includes('Invalid Dev.to API key')) {
        toast.error(
          'Invalid API Key',
          'The API key you entered is invalid. Please check it and try again.'
        );
      } else {
        toast.error('Connection Failed', message);
      }
    } finally {
      setIsConnecting(false);
    }
  }, [apiKey, currentWorkspace?.id, toast, queryClient, onSuccess]);

  const handleClose = useCallback(() => {
    if (!isConnecting) {
      setApiKey('');
      onClose();
    }
  }, [isConnecting, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && apiKey.trim() && !isConnecting) {
        e.preventDefault();
        handleConnect();
      }
    },
    [apiKey, isConnecting, handleConnect]
  );

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={!isConnecting}
      closeOnEscape={!isConnecting}
      aria-labelledby="connect-devto-title"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600">
            <PlatformIcon platform="devto" size="md" />
          </div>
          <div>
            <h2
              id="connect-devto-title"
              className="text-lg font-semibold text-dark-900 dark:text-dark-100"
            >
              Connect DEV Community
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Connect using your Dev.to API key
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Security notice */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 mb-5">
          <Shield className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
              Secure Connection
            </p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
              Your API key is stored securely and encrypted. You can revoke it
              at any time from your{' '}
              <a
                href="https://dev.to/settings/extensions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Dev.to settings
              </a>
              .
            </p>
          </div>
        </div>

        {/* How to get API key */}
        <div className="mb-5 p-3 rounded-lg bg-dark-50 dark:bg-dark-800/50 border border-dark-200 dark:border-dark-700">
          <p className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-2">
            How to get your API key:
          </p>
          <ol className="text-xs text-dark-500 dark:text-dark-400 space-y-1 list-decimal list-inside">
            <li>
              Go to{' '}
              <a
                href="https://dev.to/settings/extensions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 hover:text-primary-600 underline"
              >
                dev.to/settings/extensions
                <ExternalLink className="w-3 h-3 inline ml-0.5" />
              </a>
            </li>
            <li>Scroll down to the &quot;DEV Community API Keys&quot; section</li>
            <li>Enter a description (e.g., &quot;Mellify&quot;) and click &quot;Generate API Key&quot;</li>
            <li>Copy the generated key and paste it below</li>
          </ol>
        </div>

        {/* API Key input */}
        <div className="mb-5">
          <label
            htmlFor="devto-api-key"
            className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1.5"
          >
            API Key
          </label>
          <div className="relative">
            <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <Input
              id="devto-api-key"
              type="password"
              placeholder="Enter your Dev.to API key"
              value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKey(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isConnecting}
              className="pl-10"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Permissions list */}
        <div>
          <h3 className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-3">
            Mellify will be able to:
          </h3>
          <div className="space-y-2.5">
            {permissions.map((permission, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-success-50 dark:bg-success-900/20 shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-success-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-dark-800 dark:text-dark-200">
                    {permission.label}
                  </p>
                  <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
                    {permission.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Data usage note */}
        <div className="mt-5 pt-4 border-t border-dark-200 dark:border-dark-700">
          <p className="text-xs text-dark-400 dark:text-dark-500">
            By connecting, you agree to allow Mellify to access the permissions listed above.
            You can disconnect this account at any time from the Channels page, and revoke
            your API key from Dev.to settings.
          </p>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          type="button"
          variant="outline"
          size="md"
          onClick={handleClose}
          disabled={isConnecting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={handleConnect}
          loading={isConnecting}
          disabled={!apiKey.trim()}
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ConnectDevtoModal.displayName = 'ConnectDevtoModal';